import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import {
    getBlankAnnotationId,
    getTextAnnotationId,
} from "./moduleAnswerReviewModel";
import type {
    AnswerAnnotationModel,
    AnswerBlankAnnotation,
    AnswerBlankAnnotationState,
} from "./moduleAnswerReviewModel";

interface AnnotatedAnswerProps {
    readonly model: AnswerAnnotationModel;
    readonly answerId: string;
    readonly activeAnnotationId?: string | null;
}

const blankStateClass: Record<AnswerBlankAnnotationState, string> = {
    correct: "border-success/30 bg-success/10 text-success",
    incorrect: "border-destructive/30 bg-destructive/10 text-destructive",
    empty: "border-border bg-muted/50 text-muted-foreground",
    "needs-review": "border-primary/25 bg-primary/5 text-primary",
};

function BlankAnnotationPill({
    answerId,
    blank,
    activeAnnotationId,
}: {
    readonly answerId: string;
    readonly blank: AnswerBlankAnnotation;
    readonly activeAnnotationId?: string | null;
}) {
    const { t } = useTranslation();
    const annotationId = getBlankAnnotationId(answerId, blank.index);
    const isActive = activeAnnotationId === annotationId;
    const blankLabel = t("simulation.results.reviewDialog.blankLabel", {
        number: blank.index,
        defaultValue: `Blank ${blank.index}`,
    });
    const stateLabel = {
        correct: t("simulation.results.reviewDialog.annotationCorrect", { defaultValue: "Correct" }),
        incorrect: t("simulation.results.reviewDialog.annotationIncorrect", { defaultValue: "Needs attention" }),
        empty: t("simulation.results.reviewDialog.annotationEmpty", { defaultValue: "Not filled" }),
        "needs-review": t("simulation.results.reviewDialog.annotationNeedsReview", { defaultValue: "Review" }),
    }[blank.state];

    return (
        <span
            id={annotationId}
            className={cn(
                "font-ui inline-flex min-w-[7rem] max-w-full scroll-mt-32 flex-col rounded-md border px-2.5 py-2 text-left text-xs leading-snug transition-shadow",
                blankStateClass[blank.state],
                isActive && "ring-2 ring-primary/60 ring-offset-2 ring-offset-background",
            )}
            data-annotation-id={annotationId}
            data-annotation-state={blank.state}
            data-blank-index={blank.index}
            aria-label={`${blankLabel}: ${stateLabel}`}
            title={`${blankLabel}: ${stateLabel}`}
        >
            <span className="font-ui text-[10px] font-semibold uppercase tracking-widest opacity-75">
                {blankLabel}
            </span>
            <span className="font-ui mt-0.5 break-words text-sm font-semibold">
                {blank.value ||
                    t("simulation.results.reviewDialog.emptyBlank", {
                        defaultValue: "Empty",
                    })}
            </span>
            {blank.expected && blank.state !== "correct" ? (
                <span className="font-ui mt-1 break-words text-[11px] opacity-85">
                    {t("simulation.results.reviewDialog.expectedInline", {
                        value: blank.expected,
                        defaultValue: `Expected: ${blank.expected}`,
                    })}
                </span>
            ) : null}
        </span>
    );
}

export function AnnotatedAnswer({
    model,
    answerId,
    activeAnnotationId,
}: AnnotatedAnswerProps) {
    const { t } = useTranslation();

    if (model.kind === "fill-in-blank") {
        return (
            <div className="space-y-2" data-answer-annotations="fill-in-blank">
                <div className="flex flex-wrap gap-2">
                    {model.blanks.map((blank) => (
                        <BlankAnnotationPill
                            key={blank.index}
                            answerId={answerId}
                            blank={blank}
                            activeAnnotationId={activeAnnotationId}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2" data-answer-annotations="text">
            <div className="font-ui leading-relaxed whitespace-pre-wrap">
                {model.segments.map((segment, idx) => {
                    if (segment.state === "plain") {
                        return <span key={`plain-${idx}`}>{segment.text}</span>;
                    }
                    if (segment.state === "insertion") {
                        return (
                            <span
                                key={`insertion-${idx}`}
                                className="font-ui mx-1 inline-flex rounded bg-success/10 px-1 py-0.5 text-[11px] font-semibold text-success"
                                data-annotation-state="insertion"
                            >
                                {t("simulation.results.reviewDialog.suggestedAddition", {
                                    value: segment.text,
                                    defaultValue: `Add: ${segment.text}`,
                                })}
                            </span>
                        );
                    }
                    const annotationId = getTextAnnotationId(answerId, segment.annotationIndex ?? idx + 1);
                    const isActive = activeAnnotationId === annotationId;

                    return (
                        <span key={`issue-${idx}`} className="inline">
                            <span
                                id={annotationId}
                                className={cn(
                                    "scroll-mt-32 rounded-sm border-b-2 border-destructive/70 bg-destructive/10 px-0.5 text-destructive transition-shadow",
                                    isActive && "ring-2 ring-primary/60 ring-offset-2 ring-offset-background",
                                )}
                                data-annotation-id={annotationId}
                                data-annotation-state="incorrect"
                            >
                                {segment.text}
                            </span>
                            {segment.correction ? (
                                <span className="font-ui ml-1 inline-flex rounded bg-success/10 px-1 py-0.5 text-[11px] font-semibold text-success">
                                    {t("simulation.results.reviewDialog.suggestedCorrection", {
                                        value: segment.correction,
                                        defaultValue: `→ ${segment.correction}`,
                                    })}
                                </span>
                            ) : null}
                        </span>
                    );
                })}
            </div>

            {model.insertions.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                    {model.insertions.map((insertion, idx) => (
                        <span
                            key={`${idx}-${insertion}`}
                            className="font-ui rounded-md border border-success/20 bg-success/10 px-2 py-1 text-xs font-medium text-success"
                            data-annotation-state="insertion"
                        >
                            {t("simulation.results.reviewDialog.suggestedAddition", {
                                value: insertion,
                                defaultValue: `Add: ${insertion}`,
                            })}
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

export const AnnotatedWritingAnswer = AnnotatedAnswer;
